import { gql, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import {
  ArtistMerchItemShipCompleteMutation,
  ArtistMerchItemShipCompleteMutationVariables,
  ConsoleDeliveryItemsConnectionFragmentDoc,
  ConsoleDeliveryManagementQuery,
  ConsoleDeliveryManagementQueryVariables,
  DeliveryStatus,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  const [filterDeliveryStatus, setFilterDeliveryStatus] = useState<
    DeliveryStatus[]
  >([DeliveryStatus.Arranging, DeliveryStatus.Shipped])
  const { data, refetch, loading, fetchMore } = useQuery<
    ConsoleDeliveryManagementQuery,
    ConsoleDeliveryManagementQueryVariables
  >(QUERY, {
    variables: {
      deliveryItemsStatusFilter: [
        DeliveryStatus.Arranging,
        DeliveryStatus.Shipped,
      ],
    },
    notifyOnNetworkStatusChange: true,
    returnPartialData: true,
  })

  const [
    artistMerchItemShipComplete,
    { loading: merchArtistItemShipCompleting },
  ] = useMutation<
    ArtistMerchItemShipCompleteMutation,
    ArtistMerchItemShipCompleteMutationVariables
  >(ARTIST_MERCH_ITEM_SHIP_COMPLETE)

  return {
    state: {
      hasNextPage: !!data?.artistDeliveryItems?.pageInfo.hasNextPage,
      loading,
      merchArtistItemShipCompleting,
      artist: data?.artist ?? null,
      user,
      openConfirm,
      artistId: user?.uid ?? '',
      deliveries: data?.artistDeliveryItems ?? null,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      onSetOpenConfirm: setOpenConfirm,
      async onFilter(value) {
        const deliveryItemsStatusFilter = value
          ? [value]
          : [DeliveryStatus.Arranging, DeliveryStatus.Shipped]
        setFilterDeliveryStatus(deliveryItemsStatusFilter)
        await refetch({ deliveryItemsStatusFilter })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            deliveryItemsStatusFilter: filterDeliveryStatus,
            pageInput: {
              cursor: data?.artistDeliveryItems.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onSubmit(values) {
        await artistMerchItemShipComplete({
          variables: { input: { deliveryItemIds: values } },
        })
        setOpenConfirm(false)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleDeliveryManagement(
    $pageInput: PageInput
    $deliveryItemsStatusFilter: [DeliveryStatus!]
  ) {
    artistDeliveryItems(
      pageInput: $pageInput
      statusFilter: $deliveryItemsStatusFilter
    ) {
      ...ConsoleDeliveryItemsConnection
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
    }
  }
  ${ConsoleDeliveryItemsConnectionFragmentDoc}
`

const ARTIST_MERCH_ITEM_SHIP_COMPLETE = gql`
  mutation ArtistMerchItemShipComplete(
    $input: ArtistMerchItemShipCompleteInput!
  ) {
    artistMerchItemShipComplete(inputs: $input) {
      id
      status
      serialId
    }
  }
`
